import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@clerk/react";
import { API, errorMessage } from "@/lib/api";

const WorkspaceCtx = createContext(null);

export function WorkspaceProvider({ children }) {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgId, setOrgId] = useState(() => localStorage.getItem("sw_org"));
  const orgRef = useRef(orgId);
  orgRef.current = orgId;

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use(async (cfg) => {
      const token = await getToken();
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      if (orgRef.current) cfg.headers["X-Org-Id"] = orgRef.current;
      return cfg;
    });
    return instance;
  }, [getToken]);

  const reload = useCallback(async (preferOrg) => {
    try {
      const { data } = await api.get("/me");
      const ids = data.memberships.map((m) => m.organisation_id);
      const next = preferOrg && ids.includes(preferOrg) ? preferOrg : ids.includes(orgRef.current) ? orgRef.current : ids[0] || null;
      orgRef.current = next;
      setOrgId(next);
      setMe(data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { if (orgId) localStorage.setItem("sw_org", orgId); }, [orgId]);

  const membership = me?.memberships.find((m) => m.organisation_id === orgId);
  const role = membership?.role;
  const can = useCallback((perm) => Boolean(role && me?.permissions[perm]?.includes(role)), [role, me]);
  const switchOrg = (id) => { orgRef.current = id; setOrgId(id); };

  const value = {
    api, me, user: me?.user, memberships: me?.memberships || [], orgId, org: membership?.organisation,
    role, roleLabel: membership?.role_label, roles: me?.roles || {}, can, switchOrg, reload, loading, error,
  };
  return <WorkspaceCtx.Provider value={value}>{children}</WorkspaceCtx.Provider>;
}

export const useWorkspace = () => useContext(WorkspaceCtx);
